import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  ClipboardList, Loader2, RefreshCw, ShieldCheck, ShieldOff,
  Lock, UserCog, Settings, Phone, CheckCircle2, XCircle,
  Ban, PlayCircle, PauseCircle, Zap, Image, RotateCcw, Filter,
} from "lucide-react";
import { toast } from "sonner";
import { PaginationControls } from "../components/Pagination";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_CONFIG = {
  change_password:        { label: "Changed Password",          icon: Lock,          color: "text-amber-400",   bg: "bg-amber-500/10" },
  update_profile:         { label: "Updated Profile",           icon: UserCog,       color: "text-blue-400",    bg: "bg-blue-500/10" },
  update_settings:        { label: "Updated Settings",          icon: Settings,      color: "text-cyan-400",    bg: "bg-cyan-500/10" },
  upload_logo:            { label: "Uploaded Logo",             icon: Image,         color: "text-violet-400",  bg: "bg-violet-500/10" },
  manual_valid:           { label: "Manual Validation",         icon: CheckCircle2,  color: "text-emerald-400", bg: "bg-emerald-500/10" },
  reanalyze_call:         { label: "Reanalyzed Call",           icon: RotateCcw,     color: "text-blue-400",    bg: "bg-blue-500/10" },
  reanalyze_all:          { label: "Reanalyzed All Calls",      icon: RotateCcw,     color: "text-indigo-400",  bg: "bg-indigo-500/10" },
  block_caller:           { label: "Blocked Caller",            icon: Ban,           color: "text-red-400",     bg: "bg-red-500/10" },
  unblock_caller:         { label: "Unblocked Caller",          icon: PlayCircle,    color: "text-green-400",   bg: "bg-green-500/10" },
  pause_publisher:        { label: "Paused Publisher",          icon: PauseCircle,   color: "text-orange-400",  bg: "bg-orange-500/10" },
  activate_publisher:     { label: "Activated Publisher",       icon: PlayCircle,    color: "text-emerald-400", bg: "bg-emerald-500/10" },
  exclude_publisher:      { label: "Excluded Publisher",        icon: ShieldOff,     color: "text-red-400",     bg: "bg-red-500/10" },
  include_publisher:      { label: "Included Publisher",        icon: ShieldCheck,   color: "text-green-400",   bg: "bg-green-500/10" },
  auto_block_fraud:       { label: "Auto-Blocked Fraud",        icon: Zap,           color: "text-red-400",     bg: "bg-red-500/10" },
  auto_pause_adult:       { label: "Auto-Paused Adult Pubs",    icon: Zap,           color: "text-orange-400",  bg: "bg-orange-500/10" },
  update_action_settings: { label: "Updated Action Toggles",    icon: Filter,        color: "text-cyan-400",    bg: "bg-cyan-500/10" },
  enable_2fa:             { label: "Enabled 2FA",               icon: ShieldCheck,   color: "text-emerald-400", bg: "bg-emerald-500/10" },
  disable_2fa:            { label: "Disabled 2FA",              icon: ShieldOff,     color: "text-red-400",     bg: "bg-red-500/10" },
};

const DEFAULT_CONFIG = { label: "Action", icon: ClipboardList, color: "text-slate-400", bg: "bg-slate-500/10" };

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch { return "—"; }
}

function timeAgo(iso) {
  if (!iso) return "";
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  } catch { return ""; }
}

export default function ActivityLogPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 50;

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/activity-log?limit=5000`);
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
      setCurrentPage(1);
    } catch {
      toast.error("Failed to load activity log");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLogs(); }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="activity-log-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-100" style={{ fontFamily: "Outfit, sans-serif" }}>
            Activity Log
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            History of all your manual actions ({total} total)
          </p>
        </div>
        <button
          onClick={fetchLogs}
          className="flex items-center gap-1.5 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg px-3 py-1.5 transition-all self-start sm:self-auto"
          data-testid="refresh-activity-log"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          <span className="text-sm">Refresh</span>
        </button>
      </div>

      <Card className="bg-slate-900/60 border-slate-800/50">
        <CardContent className="p-0">
          {logs.length === 0 ? (
            <div className="text-center py-16 text-slate-500">
              <ClipboardList className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm font-medium">No activity yet</p>
              <p className="text-xs mt-1">Your manual actions will appear here</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-800/40">
              {logs.slice((currentPage - 1) * perPage, currentPage * perPage).map((log) => {
                const cfg = ACTION_CONFIG[log.action] || DEFAULT_CONFIG;
                const Icon = cfg.icon;
                return (
                  <div
                    key={log.id}
                    className="flex items-start gap-3.5 px-4 sm:px-5 py-3.5 hover:bg-white/[0.02] transition-colors"
                    data-testid={`activity-row-${log.id}`}
                  >
                    <div className={`flex-shrink-0 h-8 w-8 rounded-lg ${cfg.bg} flex items-center justify-center mt-0.5`}>
                      <Icon className={`h-4 w-4 ${cfg.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-sm font-medium ${cfg.color}`}>{cfg.label}</span>
                        {log.target && (
                          <Badge variant="outline" className="text-[10px] border-slate-700 text-slate-400 font-mono px-1.5 py-0 h-5">
                            {log.target.length > 24 ? log.target.slice(0, 24) + "..." : log.target}
                          </Badge>
                        )}
                      </div>
                      {log.details && (
                        <p className="text-xs text-slate-500 mt-0.5 truncate max-w-md">{log.details}</p>
                      )}
                    </div>
                    <div className="flex-shrink-0 text-right">
                      <p className="text-[11px] text-slate-500">{timeAgo(log.created_at)}</p>
                      <p className="text-[10px] text-slate-600 mt-0.5">{formatDateTime(log.created_at)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
      <PaginationControls currentPage={currentPage} totalPages={Math.max(1, Math.ceil(logs.length / perPage))} totalItems={logs.length} setCurrentPage={setCurrentPage} testIdPrefix="activity-pagination" />
    </div>
  );
}
